import { v4 as uuidv4 } from 'uuid';

import {
  type InputFieldConfig,
  type SimpleEventLegacy,
  type DerivedEventLegacy,
  type IndexDateEvent,
  ActiveCohortBuilderItemLegacy,
  ActiveIndexDateItemLegacy,
  DerivedEventTypeLegacy,
  IndexDateTypeLegacy,
} from '@verantos-dev/types-cohorts';

import { buildIndexDateConfigMap } from './buildConfigMap';

const mapPositionToOccurrence = (position?: string) => {
  switch (position) {
    case 'MIN':
      return 'FIRST';
    case 'MAX':
      return 'LAST';
    case 'INDEX':
      return 'NTH';
    default:
      return undefined;
  }
};

// For backwards compatibility with instanced events
// Populate the configs of instance event that was used for the index date
// This should happen only when FF is ON
const getInstancedEventOfIndexEvent = (
  indexDateValue?: string,
  derivedEvents?: DerivedEventLegacy[]
) => {
  if (!indexDateValue) return undefined;
  const instancedEvent = derivedEvents?.find(
    (event) => event.name === indexDateValue?.split('::')[1].replace(/"/g, '')
  );
  if (instancedEvent?.type !== DerivedEventTypeLegacy.INSTANCE) return undefined;
  return {
    name: instancedEvent?.eventFilters[0]?.eventName.value,
    occurrence: mapPositionToOccurrence(instancedEvent?.eventFilters[0]?.position?.value),
    dateAttribute: instancedEvent?.eventFilters[0]?.eventDateAttribute.value,
    position: instancedEvent?.eventFilters[0]?.index?.value,
  };
};

export const addOrMutateIndexDateEvent = (
  indexDate: ActiveCohortBuilderItemLegacy,
  derivedEvents: DerivedEventLegacy[]
): ActiveCohortBuilderItemLegacy => {
  const existingIndexDateEvent = (indexDate as ActiveIndexDateItemLegacy).indexDateEvent;

  const instancedEventOfIndexEvent =
    (indexDate as ActiveIndexDateItemLegacy).type === IndexDateTypeLegacy.EVENT
      ? getInstancedEventOfIndexEvent(
          (indexDate as ActiveIndexDateItemLegacy).indexDateValue,
          derivedEvents
        )
      : undefined;

  const indexDateEvent: IndexDateEvent = {
    eventName: existingIndexDateEvent?.eventName || {
      id: uuidv4(),
      value: instancedEventOfIndexEvent?.name || '',
    },
    eventDateAttribute: existingIndexDateEvent?.eventDateAttribute || {
      id: uuidv4(),
      value: instancedEventOfIndexEvent?.dateAttribute || '',
    },
    occurrence: existingIndexDateEvent?.occurrence || {
      id: uuidv4(),
      value: instancedEventOfIndexEvent?.occurrence || 'FIRST',
    },
    position: existingIndexDateEvent?.position || {
      id: uuidv4(),
      value: instancedEventOfIndexEvent?.position || '',
    },
  };

  return {
    ...indexDate,
    // Clear the old index date value to avoid conflicts
    indexDateValue: instancedEventOfIndexEvent
      ? undefined
      : (indexDate as ActiveIndexDateItemLegacy).indexDateValue,
    indexDateEvent,
  };
};

export const reduceIndexDateConfigMap = (
  acc: Record<string, InputFieldConfig>,
  {
    indexDate,
    simpleEvents,
    derivedEvents,
  }: {
    indexDate: ActiveCohortBuilderItemLegacy;
    simpleEvents: SimpleEventLegacy[];
    derivedEvents: DerivedEventLegacy[];
  }
): Record<string, InputFieldConfig> => {
  const configMap = buildIndexDateConfigMap(indexDate, simpleEvents, derivedEvents);
  return { ...acc, ...configMap };
};
